/**
 * Lớp vận chuyển tới Account REST API của Keycloak (`/realms/platform/account`).
 *
 * Đây là đường **tạm** cho màn "Tài khoản của tôi" (ADR-0027, ADR-0028): token
 * của hai cổng mang audience `account` và role `manage-account` từ
 * `default-roles-platform`, nên gọi thẳng được từ trình duyệt (CORS theo web
 * origin của client). ADR-0027 bước 1 chỉ tắt `account-console` (giao diện) và
 * giữ client `account` cùng `manage-account`, nên đường này vẫn chạy. Khi tắt
 * nốt client `account`, phiên đăng nhập và danh sách OTP chuyển sang
 * user-service, chỉ phải sửa `tai-khoan.repository.ts`.
 *
 * Lỗi ra khỏi đây là `ApiError` như `http.ts`, để `useLoi` dịch cùng một kiểu.
 */
import { duongDanAccountApi, getAccessToken } from '../auth/oidc';
import type { ApiError } from '../errors';

function maTheoHttp(status: number): string {
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'PERMISSION_DENIED';
  if (status === 404) return 'NOT_FOUND';
  if (status >= 502 && status <= 504) return 'NETWORK_ERROR';
  return 'INTERNAL';
}

export async function goiAccountApi<T>(
  duongDan: string,
  method: 'GET' | 'DELETE' = 'GET',
): Promise<T> {
  const goc = duongDanAccountApi();
  if (!goc) throw { code: 'UNAUTHENTICATED', status: 401, message: '' } satisfies ApiError;

  let token: string | undefined;
  try {
    token = await getAccessToken();
  } catch {
    throw { code: 'UNAUTHENTICATED', status: 401, message: '' } satisfies ApiError;
  }
  if (!token) throw { code: 'UNAUTHENTICATED', status: 401, message: '' } satisfies ApiError;

  let res: Response;
  try {
    res = await fetch(`${goc}${duongDan}`, {
      method,
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
  } catch {
    throw { code: 'NETWORK_ERROR', message: '' } satisfies ApiError;
  }

  // Thân lỗi của Keycloak là chữ tiếng Anh nội bộ, không phải Problem Details:
  // chỉ giữ mã theo HTTP để màn hình nói câu của catalog.
  if (!res.ok) throw { code: maTheoHttp(res.status), status: res.status, message: '' } satisfies ApiError;
  if (res.status === 204 || method === 'DELETE') return undefined as T;

  try {
    return (await res.json()) as T;
  } catch {
    throw { code: 'INTERNAL', status: res.status, message: '' } satisfies ApiError;
  }
}
